import type { HilPresentationRequestEnvelope } from '@bizfirst/hil-types';
import { HilEnvelopeValidator } from '../validator/HilEnvelopeValidator';
import { HIL_DEV_CONFIG } from '../config/HilDevConfig';

export interface HilEngineEvents {
  onEventAdded: (event: HilPresentationRequestEnvelope, queue: HilPresentationRequestEnvelope[]) => void;
  onEventResolved: (event: HilPresentationRequestEnvelope, queue: HilPresentationRequestEnvelope[]) => void;
  onQueueEmpty: () => void;
  onValidationError?: (errors: string[], raw: unknown) => void;
}

export class HilEngine {
  private queue: HilPresentationRequestEnvelope[] = [];
  private readonly handlers: HilEngineEvents;

  constructor(handlers: HilEngineEvents) {
    this.handlers = handlers;
  }

  push(raw: unknown): void {
    let payload = raw;

    if (HIL_DEV_CONFIG.isDummyEnabled && HIL_DEV_CONFIG.dummyMode) {
      console.warn('[HIL DEV] dummy mode active:', HIL_DEV_CONFIG.dummyMode);
    }

    const result = HilEnvelopeValidator.validate(payload);
    if (!result.ok) {
      this.handlers.onValidationError?.(result.errors, payload);
      return;
    }

    const envelope = result.data!;
    this.queue.push(envelope);
    this.handlers.onEventAdded(envelope, [...this.queue]);
  }

  // correlationID disambiguates entries that share the same EngageSessionID — always the case
  // for the no-Engage resume path (e.g. Octopus AI Agent chat re-suspending on EngageSessionID
  // === 0 every turn), where this queue can legitimately hold more than one envelope with the
  // same ID at once. Without it, a stale resolve for an older turn could pop a newer, still-
  // unanswered envelope instead — see the isMatch comment in hilStore.ts's `_resolveEvent` for
  // the full failure mode this caused downstream. Optional so real-Engage callers (Form/Approval
  // — unique EngageSessionID per session already) are unaffected.
  resolve(engageSessionID: number, correlationID?: string): void {
    const index = this.queue.findIndex(
      e =>
        e.Session.EngageSessionID === engageSessionID &&
        (correlationID === undefined || e.Session.CorrelationID === correlationID)
    );
    if (index === -1) return;
    const [resolved] = this.queue.splice(index, 1);
    this.handlers.onEventResolved(resolved, [...this.queue]);
    if (this.queue.length === 0) {
      this.handlers.onQueueEmpty();
    }
  }

  getActive(): HilPresentationRequestEnvelope | null {
    return this.queue[0] ?? null;
  }

  getQueue(): HilPresentationRequestEnvelope[] {
    return [...this.queue];
  }

  getQueueCount(): number {
    return this.queue.length;
  }

  clear(): void {
    this.queue = [];
    this.handlers.onQueueEmpty();
  }
}
